// Tabla.tsx
import React from 'react';

import { styles } from './style';
import Constants from '../../constants';
import CommonTable, { Column } from '../commonTable';
import Card from '../card';
import { Badge } from 'react-bootstrap';
import Utils from '../../utils/utils';
import EnrollmentNotesTimeline from '../enrollment/enrollmentNotesTimeline';
import TiraDeMeses, { leyendaTira } from '../acuerdos/tiraDeMeses';
import { horasAmPm } from "../../utils/fechaHoraAmPm";
import FranjaEnCamino from './franjaEnCamino';
import { TuitionDTO } from '../../models/TuitionDTO';

export interface AccountStatementCardProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * Columns to show in the Table
     */
    columns: Column[];
    /**
     * Data to show in the Table
     */
    data: any;
    /** Eximir meses (Supervisión de Caja y superadmin): sin esto no sale el botón. */
    onEximir?: () => void;
    /** Cambiar Promoción (Caja, Supervisión de Caja, Dirección de Ventas): sin esto no sale el botón. */
    onCambiarPromo?: () => void;
    /** «Recorrer Pagos» (Supervisión de Caja, Fase 2): sin esto no sale el botón. */
    onRecorrer?: () => void;
    /** «Cambiar Monto» de la inscripción (Caja del plantel): sin esto no sale en el renglón. */
    onCambiarInscripcion?: (t: TuitionDTO) => void;
    /** Limpieza sin hacer de esta matrícula: aviso y «Revisar Promoción» (abre su Limpieza). Sin esto no sale. */
    limpieza?: { canDecide: boolean; onRevisar: () => void };
}

/** La inscripción sin pagos ni abonos (la de $0 «pagada» sin dinero también): Caja le puede subir el monto. */
export const inscripcionEditable = (t: TuitionDTO): boolean =>
    t.typeId === 0 && t.statusId !== 1 && t.statusId !== 4 && (Number(t.paidAmount?.value) || 0) <= 0.009;

/** Bloque de un diplomado por bloques (Belleza General, Hotelería), como lo manda ProgramaMatricula::bloques. */
interface BloquePrograma { name: string; parallel: boolean; statusId: number; status: string; isCurrent: boolean; scheduleCode: string | null; scheduleTimes: string | null; periodName: string | null }
/** Horario extra de la matrícula: rotativa, Inglés en paralelo o cortesía (ProgramaMatricula::otrosHorarios). */
interface OtroHorario { kind: string; label: string; code: string; times: string; levelName: string; periodName: string }

const pildora = (tono: "actual" | "listo" | "normal" | "falta"): React.CSSProperties => ({
    fontSize: 13, padding: "3px 10px", borderRadius: 999, border: "1px solid var(--wa-filo)",
    background: tono === "actual" ? "var(--wa-acento-suave)" : "transparent",
    color: tono === "actual" ? "var(--wa-acento-texto)" : tono === "falta" ? "var(--wa-apagado)" : "inherit",
    borderStyle: tono === "falta" ? "dashed" : "solid"
});

const AccountStatementCard: React.FC<AccountStatementCardProps> = ({ columns, data, onEximir, onCambiarPromo, onRecorrer, onCambiarInscripcion, limpieza }) => {
    // El renglón de la inscripción lleva «Cambiar Monto» bajo el concepto (solo Caja, solo sin pagos).
    const columnas: Column[] = !onCambiarInscripcion ? columns : columns.map((c) => c.id !== "monthNumber" ? c : {
        ...c,
        render: (t: TuitionDTO) => {
            const base = c.render ? c.render(t) : (t as any)[c.accessor as string];
            return !inscripcionEditable(t) ? base : (
                <div>
                    {base}
                    <div>
                        <button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: 13, fontWeight: 600 }}
                            onClick={(e) => { e.stopPropagation(); onCambiarInscripcion(t); }}>Cambiar Monto</button>
                    </div>
                </div>
            );
        }
    });
    // Diplomado por bloques: el curso es el del programa y el horario es el de uno de sus bloques (Esteban, 2026-10-30).
    const esPrograma = !!(data && data.programBlocks && data.programBlocks.length);
    // El Inglés en paralelo ya sale entre los bloques.
    const otrosHorarios: OtroHorario[] = ((data && data.otherSchedules) || []).filter((h: OtroHorario) => !(esPrograma && h.kind === "paralelo"));

    /*
        AQUI SE ESCONDIA LA INSCRIPCION EN CERO, Y SE DESHIZO EL 2026-09-21.

        La idea era limpiar: una inscripcion gratis dejaba un renglon con L0.00 en
        las tres columnas y hacia dudar de si faltaba algo por pagar. Pero en caja
        ese renglon se usa: Katerin (Tegucigalpa) lo selecciona para ponerle las
        observaciones del cobro, y sin el no podia registrar la matricula. Ademas
        el renglon seguia entrando al carrito aunque ya no se viera, asi que
        aparecia un concepto que no se podia entender ni quitar desde la lista.

        Esconder algo que se sigue usando por detras es peor que el renglon feo.
        Si se quiere volver a limpiar, hay que sacarlo TAMBIEN del carrito, y eso
        ya es tocar el cobro.
    */

    return (
        <React.Fragment > {data ?
            <Card>
                <section style={{
                    ...styles.header,
                    flexDirection: "column"
                }}>
                    <div style={{
                        marginLeft: "7px",
                        display: "flex",
                        justifyContent: "flex-end"
                    }}>
                        <div style={{
                            display: "flex",
                            paddingRight: "20px",
                            flexDirection: "column"
                        }}>
                            <div style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                paddingRight: "20px"
                            }}>
                                <Badge bg={Utils.renderStatusEnrollment(data.enrollmentStatusId)}>
                                    {data.enrollmentStatus}
                                </Badge>
                            </div>

                        </div>
                    </div>
                    <div style={{
                        marginLeft: "7px",
                        display: "flex",
                        flexDirection: "column"
                    }}>
                        <p style={{
                            ...styles.subtitle,
                            margin: 0
                        }}>
                            {Constants.Common.course}:
                        </p>
                        <h3 style={styles.courseText}>
                            {data.courseName}
                        </h3>
                    </div>
                </section>
                <section style={{
                    marginBottom: 10,
                    marginLeft: "25px",
                    display: "inline-flex",
                    alignItems: "stretch",
                    flexFlow: "wrap"
                }}>
                    <div style={{ flexGrow: 1 }}>
                        <div style={{
                            display: "flex",
                            alignItems: "stretch",
                            flexFlow: "wrap"
                        }}>
                            <div style={{ flexGrow: 1 }}>
                                <div style={{
                                    ...styles.subtitleContainer,
                                    alignItems: "stretch"
                                }}>
                                    <div style={{ ...styles.subtitle }}>
                                        {esPrograma ? "Bloque Actual" : Constants.Common.level}:
                                    </div>
                                    <div style={{ ...styles.boldText }}>
                                        {data.levelName}
                                    </div>
                                </div>
                            </div>
                            <div style={{ flexGrow: 1 }}>
                                <div style={{
                                    ...styles.subtitleContainer,
                                    alignItems: "stretch"
                                }}>
                                    <div style={{ ...styles.subtitle }}>
                                        {Constants.Common.period}:
                                    </div>
                                    <div style={{ ...styles.periodText }}>
                                        {data.periodName}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ flexGrow: 1 }}>
                        <div style={{
                            display: "flex",
                            alignItems: "stretch",
                            flexFlow: "wrap"
                        }}>
                            <div style={{ flexGrow: 1 }}>
                                <div style={{
                                    ...styles.subtitleContainer,
                                    alignItems: "stretch"
                                }}>
                                    <div style={{ ...styles.subtitle }}>
                                        {Constants.Common.scheduleCode}:
                                    </div>
                                    <div style={{ ...styles.boldText }}>
                                        {data.scheduleCode}
                                    </div>
                                </div>
                            </div>
                            <div style={{ flexGrow: 1 }}>
                                <div style={{
                                    ...styles.subtitleContainer,
                                    alignItems: "stretch"
                                }}>
                                    <div style={{ ...styles.subtitle }}>
                                        {Constants.Common.schedule}:
                                    </div>
                                    <div style={{ ...styles.boldText }}>
                                        {horasAmPm(data.schedules)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                {esPrograma || otrosHorarios.length || data.programHint ? (
                    <section style={{ margin: "0 25px 14px 25px", textAlign: "left", display: "flex", flexDirection: "column", gap: 8 }}>
                        {esPrograma ? (
                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                                <span style={{ ...styles.subtitle, marginRight: 4 }}>Bloques:</span>
                                {data.programBlocks.map((b: BloquePrograma, i: number) => (
                                    <span key={"bl" + i} style={pildora(b.isCurrent ? "actual" : b.statusId === 3 ? "listo" : b.scheduleCode ? "normal" : "falta")}
                                        title={b.scheduleCode ? [b.scheduleCode, horasAmPm(b.scheduleTimes), b.periodName].filter(Boolean).join(" · ") : undefined}>
                                        <b>{b.name}</b>{b.parallel ? " (En Paralelo)" : ""} · {b.status}
                                        {b.scheduleCode ? <span style={{ fontWeight: 400 }}> · {b.scheduleCode} · {horasAmPm(b.scheduleTimes)}{b.periodName ? " · " + b.periodName : ""}</span> : null}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                        {Array.from(new Set(otrosHorarios.map((h) => h.label))).map((label) => (
                            <div key={"oh" + label} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                                <span style={{ ...styles.subtitle, marginRight: 4 }}>{label}:</span>
                                {otrosHorarios.filter((h) => h.label === label).map((h, i) => (
                                    <span key={"oh" + label + i} style={pildora("normal")}>
                                        <b>{h.code}</b> · {horasAmPm(h.times)}
                                        <span style={{ color: "var(--wa-apagado)" }}>{[h.kind === "rotativo" ? "" : h.levelName, h.periodName].filter(Boolean).map((t) => " · " + t).join("")}</span>
                                    </span>
                                ))}
                            </div>
                        ))}
                        {data.programHint ? (
                            <div style={{ fontSize: 13, color: "var(--wa-oro)" }}>
                                La nota de la matrícula dice que es del Diplomado de {data.programHint}, pero en WAPP quedó matriculada solo en {data.courseName}: sus bloques no están registrados.
                            </div>
                        ) : null}
                    </section>
                ) : null}
                <section style={{
                    margin: "0 25px 14px 25px",
                    padding: "10px 16px",
                    background: "var(--wa-papel-3)",
                    borderLeft: "3px solid var(--main-color, var(--wa-acento))",
                    borderRadius: "6px",
                    textAlign: "left"
                }}>
                    <EnrollmentNotesTimeline
                        notes={data.enrollmentNotesHistory || []}
                        createdAt={data.enrollmentCreatedAt}
                        createdBy={data.enrollmentCreatedBy}
                        advisors={data.enrollmentAdvisors || []}
                        legacyNotes={data.enrollmentNotes}
                        statusId={data.enrollmentStatusId}
                        status={data.enrollmentStatus}
                        updatedAt={data.enrollmentUpdatedAt}
                        updatedBy={data.enrollmentUpdatedBy}
                    />
                </section>
                {/* Acuerdo comercial de la matrícula (Etapa 3 de cobros): promo, con quién, condición y la tira de meses. */}
                {data.agreementStrip ? (
                    <section style={{ margin: "0 25px 14px 25px", textAlign: "left" }}>
                        {data.agreementStrip.agreements.map((a: any) => (
                            <div key={"ag" + a.id} style={{ fontSize: 14 }}>
                                <b>Acuerdo «{a.name}»</b>
                                <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 700, padding: "1px 7px", borderRadius: 999,
                                    background: a.statusId === 2 ? "var(--wa-oro-suave)" : "var(--wa-acento-suave)", color: a.statusId === 2 ? "var(--wa-oro)" : "var(--wa-acento-texto)" }}>{a.status}</span>
                                {a.members && a.members.length ? <span style={{ color: "var(--wa-apagado)" }}> · con {a.members.map((m: any) => m.name).join(", ")}</span> : null}
                                {a.requestId ? <span style={{ color: "var(--wa-apagado)" }}> · solicitud #{a.requestId}</span> : null}
                                {a.conditionText ? <div style={{ fontSize: 13, color: "var(--wa-oro)" }}>{a.conditionText}</div> : null}
                            </div>
                        ))}
                        <TiraDeMeses meses={data.agreementStrip.months.map((m: any) => ({ n: m.n, title: m.title, state: m.state, subTotal: m.subTotal, list: m.listAmount, note: m.note }))} compacta />
                        <div style={{ fontSize: 11, color: "var(--wa-apagado)" }}>{leyendaTira}</div>
                        <FranjaEnCamino strip={data.agreementStrip} />
                    </section>
                ) : null}
                {/* La base de un 50 % / 2x1 no lleva acuerdo propio: aquí se dice con quién está ligada (2026-11-02, caso Karen/Emily). */}
                {data.baseOf ? (
                    <section style={{ margin: "0 25px 14px 25px", textAlign: "left", fontSize: 14 }}>
                        <span style={{ marginRight: 6, fontSize: 12, fontWeight: 700, padding: "1px 7px", borderRadius: 999, background: "var(--wa-acento-suave)", color: "var(--wa-acento-texto)" }}>Paga Completo</span>
                        {data.baseOf}
                    </section>
                ) : null}
                {/* Limpieza sin hacer (2026-11-02): su promoción se decide antes de cobrar; mientras, no se cobran sus mensualidades. */}
                {limpieza ? (
                    <div style={{ margin: "0 25px 12px 25px", padding: "10px 14px", borderRadius: 8, background: "var(--wa-oro-suave)", color: "var(--wa-tinta)",
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", textAlign: "left" }}>
                        <div style={{ fontSize: 14 }}>
                            <b>Promoción por revisar.</b> Antes de cobrar sus mensualidades, confirma en Limpieza la promoción que ya tenía (o «Sin Promoción»).
                            {!limpieza.canDecide ? <span style={{ color: "var(--wa-apagado)" }}> La revisa Caja del plantel.</span> : null}
                        </div>
                        <button type="button" className="btn btn-primary btn-sm" style={{ borderRadius: 999 }} onClick={limpieza.onRevisar}>Revisar Promoción</button>
                    </div>
                ) : null}
                {onEximir || onCambiarPromo || onRecorrer ? (
                    <div style={{ margin: "0 25px 10px 25px", display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 8 }}>
                        {onCambiarPromo ? <button type="button" className="btn btn-outline-secondary btn-sm" style={{ borderRadius: 999 }} onClick={onCambiarPromo}>Cambiar Promoción</button> : null}
                        {onRecorrer ? <button type="button" className="btn btn-outline-secondary btn-sm" style={{ borderRadius: 999 }} onClick={onRecorrer}>Recorrer Pagos</button> : null}
                        {onEximir ? <button type="button" className="btn btn-outline-secondary btn-sm" style={{ borderRadius: 999 }} onClick={onEximir}>Eximir Meses</button> : null}
                    </div>
                ) : null}
                {/* En telefono las ocho columnas pasan a una tarjeta por mensualidad. */}
                <CommonTable
                    columns={columnas}
                    data={data.tuitions}
                    tarjetasEnTelefono
                />
                <div className="cardFooter">
                    <section style={styles.totalSection}>
                        <div style={styles.totalContainer}>
                            <div style={styles.totalText}>
                                {Constants.Screens.Finances.accountStatement.total}:
                            </div>
                            <div
                                style={{
                                    ...styles.totalTextNumber,
                                    paddingLeft: "15px",
                                    color: data.pendingAmountTotal.value === 0 ? "var(--wa-tinta)" : "var(--wa-rojo)"
                                }}>
                                {data.pendingAmountTotal.moneyFormat}
                            </div>
                        </div>
                    </section>
                </div>
            </Card>
            :
            <Card>
                <div style={{ ...styles.header }}>
                    <p>Sin registros</p>
                </div>
            </Card>
        }
        </React.Fragment>
    )
}

export default AccountStatementCard;
